// M9 / M17 — tunable game-wide constants. Per-pool HP/mana gain at each
// level-up. The gain is computed from the player's stats AT the moment of
// the level-up and added to `maxHpBase` / `maxManaBase`, persisted
// per-character. Stat allocations between levels do NOT retroactively grow
// the pool — they bank for the next level (so investing early compounds).
//
// M17 (REFONTE-STATS §3): the gain is now driven by a 75/25 pair of stats,
// and only the INVESTED part of a stat cotises (stat − 10). The base-10
// starting value contributes nothing, otherwise birth stats would saturate
// the curves over hundreds of levels. A `baseGain` floor keeps a build that
// never touches CON/INT/FAI growing at every level.
//
// More config fields can layer on later (combat multipliers, regen tempo,
// XP curve coefficients…). The schema is a single object, not a list, so
// it ships through the existing admin "publish" pipe as a one-of catalogue.

import { z } from "zod";
import type { CharacterStats } from "../types.js";

// Starting value of every stat. Only points invested ABOVE this cotise to
// the banked pools/procs (REFONTE-STATS §3).
export const STAT_BASE = 10;

// Points banked at a level-up for a 75/25 stat pair, invested-only.
//   pts = 0.75 × (primary − 10) + 0.25 × (secondary − 10)   (floored at 0)
export function bankPts(primary: number, secondary: number): number {
  return (
    0.75 * Math.max(0, primary - STAT_BASE) +
    0.25 * Math.max(0, secondary - STAT_BASE)
  );
}

export interface GameConfigPools {
  // Pool size at character creation BEFORE the level-1 gain is applied.
  base: number;
  // Flat gain added at every level-up, independent of stats (the floor).
  baseGain: number;
  // Gain added per banked point at every level-up.
  //   HP   driven by bankPts(CON, STR)   — CON 75 / STR 25
  //   mana driven by bankPts(INT, FAI)   — INT 75 / FAI 25 (replaces max())
  perStat: number;
}

export interface GameConfig {
  hp: GameConfigPools;
  mana: GameConfigPools;
}

// Defaults (REFONTE-STATS): at creation with starting stats (all 10) the
// banked part is 0, so the floor reproduces the legacy level-1 numbers:
//   HP   = base 50 + (baseGain 50 + perStat 5 × 0) = 100
//   mana = base 20 + (baseGain 50 + perStat 5 × 0) = 70
export const BUILTIN_GAME_CONFIG: GameConfig = {
  hp: { base: 50, baseGain: 50, perStat: 5 },
  mana: { base: 20, baseGain: 50, perStat: 5 },
};

let runtimeGameConfig: GameConfig = cloneConfig(BUILTIN_GAME_CONFIG);

function cloneConfig(c: GameConfig): GameConfig {
  return {
    hp: { ...c.hp },
    mana: { ...c.mana },
  };
}

export function getGameConfig(): GameConfig {
  return runtimeGameConfig;
}

export function setGameConfig(c: GameConfig): void {
  runtimeGameConfig = cloneConfig(c);
}

export function _resetGameConfigForTests(): void {
  runtimeGameConfig = cloneConfig(BUILTIN_GAME_CONFIG);
}

// Per-level-up pool gain. Caller is responsible for adding the result to
// the stored `maxHpBase` / `maxManaBase` (or to the initial-creation pool).
// Single source of truth so server + tests + admin preview share the formula.
export function hpGainAtLevel(stats: CharacterStats, config: GameConfig = getGameConfig()): number {
  // HP banks on CON (primary) + STR (secondary), invested-only.
  return Math.max(
    0,
    Math.round(config.hp.baseGain + config.hp.perStat * bankPts(stats.con, stats.str)),
  );
}

export function manaGainAtLevel(
  stats: CharacterStats,
  config: GameConfig = getGameConfig(),
): number {
  // M17 — mana banks on INT (primary) + FAI (secondary), invested-only.
  // Replaces the M8 max(INT, FAI) rule: pure mage = big reservoir, hybrid
  // pays for polyvalence (REFONTE-STATS §3).
  return Math.max(
    0,
    Math.round(config.mana.baseGain + config.mana.perStat * bankPts(stats.int, stats.fai)),
  );
}

// Initial pool at character creation: base + one level-gain applied as if
// the character had just reached level 1. Preserves the legacy "level-1
// hero has 100 HP with starting stats" numbers under the default config.
export function initialMaxHp(stats: CharacterStats, config: GameConfig = getGameConfig()): number {
  return config.hp.base + hpGainAtLevel(stats, config);
}

export function initialMaxMana(stats: CharacterStats, config: GameConfig = getGameConfig()): number {
  return config.mana.base + manaGainAtLevel(stats, config);
}

// Zod schema for admin publish validation.
const PoolsSchema = z
  .object({
    base: z.number().int().min(0).max(100_000),
    baseGain: z.number().int().min(0).max(10_000),
    perStat: z.number().int().min(0).max(1_000),
  })
  .strict();

export const GameConfigSchema = z
  .object({
    hp: PoolsSchema,
    mana: PoolsSchema,
  })
  .strict();
